/**
 * Filter "tokens" — the unit whose application order is kept in
 * `searchPageData.filterApplicationOrder` (Redux only). The recommendation section relaxes
 * whole groups (a token's key), ranked by their latest token (P1D-5538).
 *
 * A token is the filter's API key, e.g. `range_available_price`, or `key:value` for the
 * two multi-select filters ordered per option (`amenities:gym`, `bills_included:wifi`).
 * Pure module — no repo imports — so it stays unit-testable under the bare jest config.
 */

/** Standard (relaxable) filters, in search-panel order. Geo filters are never here. */
export const STANDARD_TOKEN_KEYS = [
  "range_available_price",
  "move_in_month",
  "leaseDuration",
  "meta_types",
  "guarantor",
  "cancellation_policy",
  "available",
  "tags",
  "meta_unit_types",
  "amenities",
  "bills_included",
] as const;

export const MOVE_IN_TOKEN = "move_in_month";

/** A multi-select value as a list, from an array or a comma-separated string. */
export const asList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === "string") return value.split(",").filter(Boolean);
  return [];
};

export const tokenKey = (token: string) => token.split(":")[0];

export const splitToken = (token: string): [string, string | undefined] => {
  const idx = token.indexOf(":");
  if (idx === -1) return [token, undefined];
  return [token.slice(0, idx), token.slice(idx + 1)];
};

export const makeToken = (key: string, value?: string) =>
  value === undefined || value === "" ? key : `${key}:${String(value).toLowerCase()}`;

export const isStandardToken = (token: string) =>
  (STANDARD_TOKEN_KEYS as readonly string[]).includes(tokenKey(token));

export const panelPosition = (token: string) =>
  (STANDARD_TOKEN_KEYS as readonly string[]).indexOf(tokenKey(token));

/**
 * Merge the previous application order with the tokens now applied: surviving tokens keep
 * their slot, new tokens append in the order given (panel order). A re-added token is new.
 */
export const updateApplicationOrder = (prev: string[] = [], nextTokens: string[] = []) => {
  const kept = prev.filter((t) => nextTokens.includes(t));
  const added = nextTokens.filter((t) => !kept.includes(t));
  return [...kept, ...added];
};

/**
 * Filter groups the recommendation section relaxes (P1D-5538 FR-5): every standard filter
 * except Availability, which is never counted or removed. A group is a token's key.
 */
export const RELAXABLE_GROUP_KEYS: readonly string[] = STANDARD_TOKEN_KEYS.filter(
  (k) => k !== "available",
);

/**
 * Filters the recommendation section never removes, so its "Applied filters" line always lists
 * them as applied. Availability is not here: the section adds sold-out properties at the end
 * whatever it is set to. University and Locality are left out, as in the radius-expand banner.
 */
export const KEPT_GROUP_KEYS: readonly string[] = [
  "housing_type",
  "region_radius",
  "amber_sale_status",
];

/** Unique relaxable groups among `tokens`, in first-seen order. */
export const appliedGroups = (tokens: string[] = []): string[] => [
  ...new Set(tokens.map(tokenKey).filter((k) => RELAXABLE_GROUP_KEYS.includes(k))),
];

/**
 * Recency of a group: the highest application-order index among its tokens (the latest
 * applied option wins, HLD D4). A token missing from the order ranks after every known one,
 * by panel position, so after a reload the order is reverse panel order.
 */
export const groupRank = (group: string, tokens: string[] = [], applicationOrder: string[] = []) =>
  tokens
    .filter((t) => tokenKey(t) === group)
    .reduce((max, t) => {
      const i = applicationOrder.indexOf(t);
      return Math.max(max, i === -1 ? applicationOrder.length + panelPosition(t) : i);
    }, -1);
